import { memo, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import type { ChatMessage } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Message, ThinkingMessage } from './message';
import { Button } from './ui/button';
import { useDataStream } from './ui/data-stream-provider';
import equal from 'fast-deep-equal';

type ChatStatus = 'ready' | 'awaiting_message' | 'submitted' | 'streaming' | 'streaming_data' | 'error';

interface MessagesProps {
  messages: Array<ChatMessage>;
  status?: ChatStatus;
  isReadonly?: boolean;
  className?: string;
}

function PureMessages({
  messages,
  status,
  className,
}: MessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);

  const isLoading = status === 'submitted' || status === 'streaming';

  // Scroll to bottom function
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    });
  };

  useDataStream();

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (isAutoScrollEnabled) {
      scrollToBottom();
    }
  }, [messages, isAutoScrollEnabled]);

  // Handle scroll events to show/hide scroll button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      setShowScrollButton(!isNearBottom);
      setIsAutoScrollEnabled(isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Manual scroll to bottom
  const handleScrollToBottom = () => {
    setIsAutoScrollEnabled(true);
    scrollToBottom('smooth');
  };

  return (
    <div ref={containerRef} className={cn('relative flex-1 overflow-y-auto', className)}>
      <div className="py-6 space-y-6">
        {messages.length === 0 ? (
          <motion.div
            className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <svg
                className="w-10 h-10 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </motion.div>
            <motion.h2
              className="text-2xl font-semibold mb-2 text-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Start a conversation
            </motion.h2>
            <motion.p
              className="text-sm max-w-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Send a message to get started. I'm here to help you with any questions or tasks.
            </motion.p>
          </motion.div>
        ) : (
          <>
            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                isLoading={isLoading && message === messages[messages.length - 1]}
              />
            ))}

            <AnimatePresence mode="wait">
              {isLoading && <ThinkingMessage key="thinking" />}
            </AnimatePresence>
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to Bottom Button */}
      <AnimatePresence>
        {showScrollButton && (
          <motion.div
            className="sticky bottom-4 left-0 right-0 flex justify-center pointer-events-none"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              onClick={handleScrollToBottom}
              size="sm"
              variant="secondary"
              className="pointer-events-auto shadow-lg"
              aria-label="Scroll to bottom"
            >
              <ArrowDown className="w-4 h-4 mr-1" />
              Scroll to bottom
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const Messages = memo(
  PureMessages,
  (prevProps, nextProps) => {
    if (prevProps.status !== nextProps.status) return false;
    if (prevProps.messages.length !== nextProps.messages.length) return false;
    if (!equal(prevProps.messages, nextProps.messages)) return false;
    return false;
  }
);

Messages.displayName = 'Messages';
